import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";
import type { Invitation, InvitationRole, Profile } from "@/types/database";
import {
  sendInvitationEmail,
  sendHostInvitationEmail,
  sendUniversalInvitationEmail,
} from "@/lib/email";
import { notifyInviterAccepted } from "@/lib/notifications";

// ─── Role-based invitation permission matrix ──────────────────────────────────
/**
 * Maps each inviter role to the set of roles they are allowed to invite.
 * The application layer enforces this; the DB RLS only checks `auth.uid() = invited_by`.
 */
export const INVITABLE_ROLES: Record<string, InvitationRole[]> = {
  strata_admin:      ["strata_admin", "property_manager", "host", "individual"],
  property_manager:  ["property_manager", "host", "individual"],
  host:              ["host", "individual"],
  individual:        ["individual"],
  system_admin:      ["strata_admin", "property_manager", "host", "individual"],
};

/** Human-readable label for each role */
export const ROLE_LABELS: Record<InvitationRole, string> = {
  strata_admin:      "Strata Admin / Council",
  property_manager:  "Property Manager",
  host:              "Host",
  individual:        "Individual Owner",
};

// ─── Org Invitations (admin view) ─────────────────────────────────────────────

/** Fetch all pending invitations for the active org (strata admin view) */
export function useOrgInvitations(orgId: string | null) {
  return useQuery({
    queryKey: ["invitations", "org", orgId],
    enabled: !!orgId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("invitations")
        .select(`
          *,
          organization:organizations(id, name),
          inviter:profiles!invitations_invited_by_fkey(id, full_name, email)
        `)
        .eq("org_id", orgId!)
        .eq("accepted", false)
        .gte("expires_at", new Date().toISOString())
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as Invitation[];
    },
  });
}

// ─── Token lookup (public, for accept-invite page) ───────────────────────────

/** Lookup a single invitation by its token (public, for the acceptance page) */
export function useInvitationByToken(token: string | null) {
  return useQuery({
    queryKey: ["invitations", "token", token],
    enabled: !!token,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("invitations")
        .select(`
          *,
          organization:organizations(id, name, address),
          unit:units!invitations_unit_id_fkey(id, unit_number, owner_name)
        `)
        .eq("token", token!)
        .single();
      if (error) throw error;
      return data as Invitation;
    },
  });
}

// ─── My Sent Invitations ──────────────────────────────────────────────────────

/**
 * Fetch all invitations sent by the current user (regardless of org).
 * Used on the "My Invitations" page.
 */
export function useSentInvitations() {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["invitations", "sent", user?.id],
    enabled: !!user?.id,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("invitations")
        .select(`
          *,
          organization:organizations(id, name),
          inviter:profiles!invitations_invited_by_fkey(id, full_name, email),
          unit:units!invitations_unit_id_fkey(id, unit_number)
        `)
        .eq("invited_by", user!.id)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as Invitation[];
    },
  });
}

// ─── My Invitees (accepted) ───────────────────────────────────────────────────

/**
 * Fetch all users who accepted an invitation from the current user.
 * Returns their full profiles so the "My Network" tab can display them.
 */
export function useMyInvitees() {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["invitations", "invitees", user?.id],
    enabled: !!user?.id,
    queryFn: async () => {
      // 1. Fetch accepted invitations sent by me that have an accepted_by_user_id
      const { data: invitations, error: invErr } = await supabase
        .from("invitations")
        .select("id, email, role, accepted_by_user_id, created_at, org_id, organization:organizations(id, name)")
        .eq("invited_by", user!.id)
        .eq("accepted", true)
        .not("accepted_by_user_id", "is", null);

      if (invErr) throw invErr;
      if (!invitations || invitations.length === 0) return [];

      // 2. Fetch profiles for accepted users
      const acceptedIds = invitations
        .map((inv: any) => inv.accepted_by_user_id)
        .filter(Boolean) as string[];

      const { data: profiles, error: profErr } = await supabase
        .from("profiles")
        .select("id, full_name, email, role, avatar_url, created_at")
        .in("id", acceptedIds);

      if (profErr) throw profErr;

      // 3. Merge invitation context with profile data
      return invitations.map((inv: any) => {
        const profile = (profiles ?? []).find((p: any) => p.id === inv.accepted_by_user_id);
        return {
          invitationId:   inv.id,
          invitedRole:    inv.role as InvitationRole,
          invitedAt:      inv.created_at,
          orgId:          inv.org_id,
          organization:   inv.organization,
          profile:        profile as Profile | undefined,
        };
      });
    },
  });
}

// ─── Send Invitation (universal) ─────────────────────────────────────────────

/** Create (send) an invitation — any authenticated user can call this.
 *  orgId is taken from the caller's active org context; pass an explicit orgId
 *  to override (e.g. when a PM is inviting on behalf of a specific building).
 */
export function useSendInvitation() {
  const { user, activeOrgId } = useAuth();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async ({
      email,
      role,
      orgId,
    }: {
      email: string;
      role: InvitationRole;
      orgId?: string;
    }) => {
      const targetOrgId = orgId ?? activeOrgId ?? null;

      // 1. Insert the invitation row (generates token automatically)
      const { data, error } = await supabase
        .from("invitations")
        .insert({
          email,
          role,
          org_id: targetOrgId,        // nullable — allowed for host/individual
          invited_by: user?.id ?? null,
        })
        .select(`
          *,
          organization:organizations(id, name),
          inviter:profiles!invitations_invited_by_fkey(id, full_name, email)
        `)
        .single();
      if (error) throw error;

      const invitation = data as Invitation;
      const orgName    = (invitation.organization as any)?.name ?? undefined;
      const inviterName = (invitation.inviter as any)?.full_name ?? user?.fullName ?? "A Stratakey user";
      const acceptUrl  = `${window.location.origin}/accept-invite?token=${invitation.token}`;

      // 2. Send appropriate email (fire-and-forget)
      if (role === "property_manager" || role === "strata_admin") {
        // Use the existing styled invitation email for admin-level roles
        sendInvitationEmail({
          inviteeEmail: email,
          orgName:      orgName ?? "Stratakey",
          role,
          inviterName,
          acceptUrl,
          expiresAt:    invitation.expires_at,
        }).catch((e) => console.warn("[email] Invitation email failed:", e));
      } else {
        // Use the new universal template for host / individual
        sendUniversalInvitationEmail({
          inviteeEmail: email,
          role,
          inviterName,
          orgName,
          acceptUrl,
          expiresAt:    invitation.expires_at,
        }).catch((e) => console.warn("[email] Universal invitation email failed:", e));
      }

      return invitation;
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ["invitations", "org", vars.orgId ?? activeOrgId] });
      qc.invalidateQueries({ queryKey: ["invitations", "sent"] });
    },
  });
}

// ─── Send Host Invitation (with unit assignment) ──────────────────────────────

/** Create (send) a host invitation with a pre-assigned unit — property manager / strata admin */
export function useSendHostInvitation() {
  const { user, activeOrgId } = useAuth();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async ({
      email,
      unitId,
      unitNumber,
      nightLimit = 90,
      orgId,
    }: {
      email: string;
      unitId: string;
      unitNumber: string;
      nightLimit?: number;
      orgId?: string;
    }) => {
      const targetOrgId = orgId ?? activeOrgId;
      if (!targetOrgId) throw new Error("No active org");

      // 1. Insert invitation row with unit_id and night_limit
      const { data, error } = await supabase
        .from("invitations")
        .insert({
          email,
          role: "host" as InvitationRole,
          org_id: targetOrgId,
          invited_by: user?.id ?? null,
          unit_id: unitId,
          night_limit: nightLimit,
        })
        .select(`
          *,
          organization:organizations(id, name),
          inviter:profiles!invitations_invited_by_fkey(id, full_name, email),
          unit:units!invitations_unit_id_fkey(id, unit_number)
        `)
        .single();
      if (error) throw error;

      const invitation = data as Invitation;
      const orgName    = (invitation.organization as any)?.name ?? "Strata";
      const inviterName = (invitation.inviter as any)?.full_name ?? "A property manager";
      const acceptUrl  = `${window.location.origin}/accept-invite?token=${invitation.token}`;

      // 2. Send host-specific invitation email (fire-and-forget)
      sendHostInvitationEmail({
        inviteeEmail: email,
        orgName,
        unitNumber,
        inviterName,
        acceptUrl,
        expiresAt:   invitation.expires_at,
        nightLimit,
      }).catch((e) => console.warn("[email] Host invitation email failed:", e));

      return invitation;
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ["invitations", "org", vars.orgId ?? activeOrgId] });
      qc.invalidateQueries({ queryKey: ["invitations", "sent"] });
    },
  });
}

// ─── Revoke Invitation ────────────────────────────────────────────────────────

/** Revoke (delete) a pending invitation */
export function useRevokeInvitation() {
  const { user, activeOrgId } = useAuth();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (invitationId: string) => {
      const { error } = await supabase
        .from("invitations")
        .delete()
        .eq("id", invitationId);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["invitations", "org", activeOrgId] });
      qc.invalidateQueries({ queryKey: ["invitations", "sent", user?.id] });
      qc.invalidateQueries({ queryKey: ["invitations", "invitees", user?.id] });
    },
  });
}

// ─── Accept Invitation ────────────────────────────────────────────────────────

/**
 * Accept an invitation — marks it as accepted, creates user_assignment (if org is set),
 * creates hosts record (for host role), and records accepted_by_user_id.
 */
export function useAcceptInvitation() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async ({
      token,
      userId,
    }: {
      token: string;
      userId: string;
    }) => {
      // 1. Look up the invitation
      const { data: inv, error: invError } = await supabase
        .from("invitations")
        .select("*")
        .eq("token", token)
        .eq("accepted", false)
        .single();

      if (invError || !inv) throw new Error("Invitation not found or already accepted");

      // 2. Check expiry
      if (new Date(inv.expires_at) < new Date()) {
        throw new Error("This invitation has expired");
      }

      // 3. Create user_assignment — only if the invitation has an org context
      if (inv.org_id) {
        const { error: assignError } = await supabase
          .from("user_assignments")
          .insert({
            user_id:    userId,
            org_id:     inv.org_id,
            role:       inv.role === "individual" ? "host" : inv.role, // map individual → host assignment or keep original
            unit_id:    inv.unit_id ?? null,
            permissions: {},
          });

        if (assignError && !assignError.message.includes("duplicate")) {
          throw assignError;
        }
      }

      // 4. If this is a host invitation with a unit, create the hosts record
      if (inv.role === "host" && inv.unit_id && inv.org_id) {
        const { data: existingHost } = await supabase
          .from("hosts")
          .select("id")
          .eq("user_id", userId)
          .eq("unit_id", inv.unit_id)
          .eq("organization_id", inv.org_id)
          .maybeSingle();

        if (!existingHost) {
          const { error: hostError } = await supabase
            .from("hosts")
            .insert({
              user_id:         userId,
              unit_id:         inv.unit_id,
              organization_id: inv.org_id,
              status:          "pending",
              night_limit:     inv.night_limit ?? 90,
            });

          if (hostError) {
            console.warn("[useAcceptInvitation] Failed to create hosts record:", hostError.message);
          }
        }
      }

      // 5. Mark invitation as accepted and record who accepted it
      const { error: updateError } = await supabase
        .from("invitations")
        .update({
          accepted:              true,
          accepted_by_user_id:   userId,
        })
        .eq("id", inv.id);

      if (updateError) throw updateError;

      // 6. Notify the inviter (fire-and-forget)
      if (inv.invited_by) {
        const { data: acceptedProfile } = await supabase
          .from("profiles")
          .select("full_name")
          .eq("id", userId)
          .single();
        const acceptedName = acceptedProfile?.full_name ?? "Someone";
        notifyInviterAccepted(inv.invited_by, acceptedName, inv.role, inv.org_id)
          .catch((e) => console.warn("[notify] Invitation accepted notification failed:", e));
      }

      return inv as Invitation;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["invitations"] });
      qc.invalidateQueries({ queryKey: ["user_assignments"] });
      qc.invalidateQueries({ queryKey: ["hosts"] });
    },
  });
}
